
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { BulkOrderRow } from '../multiple-add-order.component';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter, take } from 'rxjs';
import { CompaniesService } from '@app/services/companies.service';
import { AddressService } from '@app/services/address.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { ShareddtaService } from '@app/services/shareddta.service';
import { ToastService } from '@app/services/toast.service';
import { DialogService } from '@app/services/dialog.service';

@Component({
    selector: '[app-bulk-order-row]',
    templateUrl: './bulk-order-row.component.html',
    styleUrls: ['./bulk-order-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class BulkOrderRowComponent implements OnInit, OnDestroy {
    @Input() row!: BulkOrderRow;
    @Input() warehouseOptions: any[] = [];
    @Input() countryCodeOptions: any[] = [];
    @Input() serviceTimeOptions: any[] = [];
    @Input() driverOptions: any[] = [];
    @Input() yesNoOptions: any[] = [];
    @Input() delpuOptions: any[] = [];
    @Input() minDate: string = '';
    @Input() defaultCountryCode: string = '';
    @Input() companies: any[] = [];
    @Input() addresses: any[] = [];
    @Input() allRows: BulkOrderRow[] = [];

    @Output() rowFocus = new EventEmitter<number>();
    @Output() delete = new EventEmitter<BulkOrderRow>();
    @Output() companySelected = new EventEmitter<any>();
    @Output() addressSelected = new EventEmitter<any>();
    @Output() openAddressDialog = new EventEmitter<any>();
    @Output() companyAdded = new EventEmitter<string>();

    private companySearchSubject = new Subject<string>();
    private addressSearchSubject = new Subject<string>();
    private subscriptions: Subscription = new Subscription();

    isCompanySearching = false;
    isAddressSearching = false;
    isAddingCompany = false;

    private lastMouseX = -1;
    private lastMouseY = -1;

    constructor(
        private companyService: CompaniesService,
        private addressService: AddressService,
        private cdr: ChangeDetectorRef,
        private authService: AuthServiceService,
        private shareddta: ShareddtaService,
        private el: ElementRef,
        private toast: ToastService,
        private dialogService: DialogService
    ) { }

    ngOnInit(): void {
        if (this.row) {
            this.row.originalCompanyName = this.row.companyName;
            this.row.originalCompanyAddress = this.row.companyAddress;
        }
        if (this.driverOptions && !this.driverOptions.some(d => d.id === 0 || d.id === '0' || d.id === null)) {
            this.driverOptions = [{ id: 0, first_name: 'None', last_name: '' }, ...this.driverOptions];
        }
        console.log('row', this.row);
        console.log('driverOptions', this.driverOptions);

        this.subscriptions.add(
            this.companySearchSubject.pipe(
                debounceTime(500),
                distinctUntilChanged(),
                filter(text => !!text && text.trim().length >= 2)
            ).subscribe(text => this.performCompanySearch(text))
        );

        this.subscriptions.add(
            this.addressSearchSubject.pipe(
                debounceTime(500),
                distinctUntilChanged(),
                filter(text => !!text && text.trim().length >= 2)
            ).subscribe(text => this.performAddressSearch(text))
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.companySearchSubject.complete();
        this.addressSearchSubject.complete();
    }

    onRowFocus(): void {
        this.rowFocus.emit(this.row.rowNumber);
        this.cdr.markForCheck();
    }

    // --- Company Dropdown Logic ---
    openCompanyDropdown(): void {
        this.row.showCompanyDropdown = true;
        this.filterCompanies();
        this.onRowFocus();
        this.cdr.markForCheck();
    }

    toggleCompanyDropdown(): void {
        if (this.row.showCompanyDropdown) {
            this.row.showCompanyDropdown = false;
            this.cdr.markForCheck();
        } else {
            this.openCompanyDropdown();
            // Focus the input so blur fires on outside clicks → closeCompanyDropdown()
            setTimeout(() => {
                const input = this.el.nativeElement.querySelector('.col-company-name input');
                input?.focus();
            });
        }
    }

    filterCompanies(): void {
        const value = this.row.companyName?.trimStart().toLowerCase() || '';
        const originalName = this.row.originalCompanyName?.trimStart().toLowerCase() || '';

        // If there are matching companies from import, and the input value is either empty or matches the original company name exactly
        if (this.row.matchingCompanies && this.row.matchingCompanies.length > 0 &&
            (!value || value === originalName)) {
            this.row.filteredCompanies = this.row.matchingCompanies.map((item: any) =>
                typeof item === 'string' ? { company: item } : { company: item.company || item.name, id: item.id }
            );
            this.row.showCompanyDropdown = this.row.filteredCompanies.length > 0;
            this.row.highlightedCompanyIndex = this.row.filteredCompanies.length > 0 ? 0 : -1;
            this.cdr.markForCheck();
            return;
        }

        // --- HYBRID LOGIC ---
        if (!value) {
            // Show initial suggestions (page 1) when empty
            this.row.filteredCompanies = this.companies || [];
            this.row.showCompanyDropdown = this.row.filteredCompanies.length > 0;
            this.row.highlightedCompanyIndex = this.row.filteredCompanies.length > 0 ? 0 : -1;
            this.row.isCompanyNew = false;
            this.cdr.markForCheck();
            return;
        }


        const localMatches = this.companies.filter(c =>
            c.company.toLowerCase().includes(value)
        );

        this.row.filteredCompanies = localMatches;
        this.row.highlightedCompanyIndex = localMatches.length > 0 ? 0 : -1;

        const exactMatch = this.companies.some(c => c.company?.toLowerCase() === value);
        this.row.isCompanyNew = !exactMatch;

        if (value.trim().length >= 2) {
            this.isCompanySearching = localMatches.length === 0;
            this.row.showCompanyDropdown = true;
            this.cdr.markForCheck();
            this.companySearchSubject.next(value);
        } else {
            this.isCompanySearching = false;
            this.row.showCompanyDropdown = localMatches.length > 0;
            this.cdr.markForCheck();
            this.companySearchSubject.next(''); // Clear pending API search
        }
    }

    performCompanySearch(text: string): void {
        this.subscriptions.add(
            this.companyService.companySearchObservable('1', text).pipe(take(1)).subscribe({
                next: (data: any) => {
                    this.isCompanySearching = false;

                    let results = data?.addresses?.results || data?.companies?.results || data?.result?.results || data?.result || data || [];
                    if (!Array.isArray(results)) results = [];

                    // Smart Caching: Add new companies to the shared array so other rows don't need to hit the API
                    results.forEach((newItem: any) => {
                        if (newItem.company && !this.companies.some(c => c.id === newItem.id || c.company === newItem.company)) {
                            this.companies.push(newItem);
                        }
                    });

                    this.row.filteredCompanies = results.filter((c: any) =>
                        c.company.toLowerCase().includes(text.toLowerCase())
                    );

                    // Only open the dropdown if the user hasn't made a selection or changed the text
                    if (this.row.companyName?.trim().toLowerCase() === text.trim().toLowerCase()) {
                        this.row.showCompanyDropdown = this.row.filteredCompanies.length > 0;
                        this.row.highlightedCompanyIndex = this.row.filteredCompanies.length > 0 ? 0 : -1;
                        const exactMatch = results.some((c: any) => c.company?.toLowerCase() === text.trim().toLowerCase());
                        this.row.isCompanyNew = !exactMatch;
                    }

                    this.cdr.detectChanges();
                },
                error: () => {
                    this.isCompanySearching = false;
                    this.row.showCompanyDropdown = false;
                    this.cdr.detectChanges();
                }
            })
        );
    }

    selectCompany(company: any): void {
        this.row.companyName = company.company;
        this.row.showCompanyDropdown = false;
        this.row.isCompanyNew = false;
        this.row.matchingCompanies = [];
        this.companySelected.emit({ company, row: this.row });
    }

    closeCompanyDropdown(): void {
        setTimeout(() => {
            this.row.showCompanyDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    // --- Address Dropdown Logic ---
    openAddressDropdown(): void {
        this.row.showAddButton = true;
        this.filterAddresses();
        this.onRowFocus();
        this.cdr.markForCheck();
    }

    toggleAddressDropdown(): void {
        if (this.row.showAddressDropdown) {
            this.row.showAddressDropdown = false;
            this.cdr.markForCheck();
        } else {
            this.openAddressDropdown();
            setTimeout(() => {
                const input = this.el.nativeElement.querySelector('.col-company-address input');
                input?.focus();
            });
        }
    }

    filterAddresses(): void {
        const value = this.row.companyAddress?.trimStart().toLowerCase() || '';
        const originalAddress = this.row.originalCompanyAddress?.trimStart().toLowerCase() || '';

        // If there are matching addresses from import, and the input value is either empty or matches the original address exactly
        if (this.row.matchingAddresses && this.row.matchingAddresses.length > 0 &&
            (!value || value === originalAddress)) {
            this.row.filteredAddressOptions = this.row.matchingAddresses.map((item: any) =>
                typeof item === 'string' ? { address: item } : { address: item.address || item.name, id: item.id }
            );
            this.row.showAddressDropdown = this.row.filteredAddressOptions.length > 0;
            this.row.highlightedAddressIndex = this.row.filteredAddressOptions.length > 0 ? 0 : -1;
            this.cdr.markForCheck();
            return;
        }

        // --- HYBRID LOGIC ---
        if (!value) {
            // Show initial suggestions (page 1) when empty
            this.row.filteredAddressOptions = this.addresses || [];
            this.row.showAddressDropdown = this.row.filteredAddressOptions.length > 0;
            this.row.highlightedAddressIndex = this.row.filteredAddressOptions.length > 0 ? 0 : -1;
            this.row.isAddressNew = false;
            this.row.addressId = null;
            this.cdr.markForCheck();
            return;
        }


        const localMatches = this.addresses.filter(a =>
            a.address.toLowerCase().includes(value)
        );

        this.row.filteredAddressOptions = localMatches;
        this.row.highlightedAddressIndex = localMatches.length > 0 ? 0 : -1;

        const matchedAddress = this.addresses.find(a => a.address?.toLowerCase() === value);
        if (matchedAddress) {
            this.row.isAddressNew = false;
            this.row.addressId = matchedAddress.id;
        } else {
            this.row.isAddressNew = true;
            this.row.addressId = null;
        }

        if (value.trim().length >= 2) {
            this.isAddressSearching = localMatches.length === 0;
            this.row.showAddressDropdown = true;
            this.cdr.markForCheck();
            this.addressSearchSubject.next(value);
        } else {
            this.isAddressSearching = false;
            this.row.showAddressDropdown = localMatches.length > 0;
            this.cdr.markForCheck();
            this.addressSearchSubject.next(''); // Clear pending API search
        }
    }

    performAddressSearch(text: string): void {
        this.subscriptions.add(
            this.addressService.addressSearchObservable('1', text).pipe(take(1)).subscribe({
                next: (data: any) => {
                    this.isAddressSearching = false;

                    let results = data?.addresses?.results || data?.companies?.results || data?.result?.results || data?.result || data || [];
                    if (!Array.isArray(results)) results = [];

                    // Smart Caching: Add new addresses to the shared array so other rows don't need to hit the API
                    results.forEach((newItem: any) => {
                        if (newItem.address && !this.addresses.some(a => a.id === newItem.id || a.address === newItem.address)) {
                            this.addresses.push(newItem);
                        }
                    });

                    this.row.filteredAddressOptions = results.filter((a: any) =>
                        a.address.toLowerCase().includes(text.toLowerCase())
                    );

                    // Only open the dropdown if the user hasn't made a selection or changed the text
                    if (this.row.companyAddress?.trim().toLowerCase() === text.trim().toLowerCase()) {
                        this.row.showAddressDropdown = this.row.filteredAddressOptions.length > 0;
                        this.row.highlightedAddressIndex = this.row.filteredAddressOptions.length > 0 ? 0 : -1;
                        const matched = results.find((a: any) => a.address?.toLowerCase() === text.trim().toLowerCase());
                        if (matched) {
                            this.row.isAddressNew = false;
                            this.row.addressId = matched.id;
                        } else {
                            this.row.isAddressNew = true;
                            this.row.addressId = null;
                        }
                    }

                    this.cdr.detectChanges();
                },
                error: () => {
                    this.isAddressSearching = false;
                    this.row.showAddressDropdown = false;
                    this.cdr.detectChanges();
                }
            })
        );
    }

    selectAddress(address: any): void {
        this.row.companyAddress = address.address;
        this.row.addressId = address.id;
        this.row.showAddressDropdown = false;
        this.row.isAddressNew = false;
        this.row.matchingAddresses = [];
        this.addressSelected.emit({ address, row: this.row });
        this.cdr.markForCheck();
    }

    closeAddressDropdown(): void {
        setTimeout(() => {
            this.row.showAddressDropdown = false;
            this.row.showAddButton = false;
            this.cdr.markForCheck();
        }, 300);
    }

    onCompanyKeydown(event: KeyboardEvent): void {
        if (!this.row.showCompanyDropdown) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.row.highlightedCompanyIndex = (this.row.highlightedCompanyIndex + 1) % this.row.filteredCompanies.length;
                this.scrollToOption();
                this.cdr.detectChanges();
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.row.highlightedCompanyIndex = (this.row.highlightedCompanyIndex - 1 + this.row.filteredCompanies.length) % this.row.filteredCompanies.length;
                this.scrollToOption();
                this.cdr.detectChanges();
                break;
            case 'Enter':
                event.preventDefault();
                if (this.row.highlightedCompanyIndex >= 0 && this.row.highlightedCompanyIndex < this.row.filteredCompanies.length) {
                    this.selectCompany(this.row.filteredCompanies[this.row.highlightedCompanyIndex]);
                }
                break;
            case 'Escape':
                this.row.showCompanyDropdown = false;
                this.cdr.detectChanges();
                break;
        }
    }

    onAddressKeydown(event: KeyboardEvent): void {
        if (!this.row.showAddressDropdown) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.row.highlightedAddressIndex = (this.row.highlightedAddressIndex + 1) % this.row.filteredAddressOptions.length;
                this.scrollToOption();
                this.cdr.detectChanges();
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.row.highlightedAddressIndex = (this.row.highlightedAddressIndex - 1 + this.row.filteredAddressOptions.length) % this.row.filteredAddressOptions.length;
                this.scrollToOption();
                this.cdr.detectChanges();
                break;
            case 'Enter':
                event.preventDefault();
                if (this.row.highlightedAddressIndex >= 0 && this.row.highlightedAddressIndex < this.row.filteredAddressOptions.length) {
                    this.selectAddress(this.row.filteredAddressOptions[this.row.highlightedAddressIndex]);
                }
                break;
            case 'Escape':
                this.row.showAddressDropdown = false;
                this.cdr.detectChanges();
                break;
        }
    }

    private scrollToOption(): void {
        setTimeout(() => {
            const activeOption = this.el.nativeElement.querySelector('.combo-dropdown .combo-option.active') as HTMLElement;
            if (activeOption) {
                activeOption.scrollIntoView({ block: 'nearest' });
            }
        });
    }

    onMouseMove(event: MouseEvent, index: number, type: 'company' | 'address'): void {
        // Prevent hover logic from firing if the mouse didn't physically move
        // (e.g. if the list just scrolled underneath the stationary mouse pointer)
        if (event.clientX === this.lastMouseX && event.clientY === this.lastMouseY) {
            return;
        }

        this.lastMouseX = event.clientX;
        this.lastMouseY = event.clientY;

        if (type === 'company') {
            this.row.highlightedCompanyIndex = index;
        } else {
            this.row.highlightedAddressIndex = index;
        }
    }

    // --- Utility Methods ---
    // isAlphaNumeric(event: any): void {
    //     const charCode = (event.which) ? event.which : event.keyCode;
    //     if (charCode > 31 && (charCode < 48 || charCode > 57) && (charCode < 65 || charCode > 90) && (charCode < 97 || charCode > 122) && charCode !== 32) {
    //         event.preventDefault();
    //     }
    // }
    isAlphaNumeric(event: any): void {
        const key = event.key;
        const input = event.target;

        const allowedKeys = [
            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'
        ];

        // ✅ Allow control keys
        if (allowedKeys.includes(key)) return;

        // ✅ Allow letters, numbers, space
        if (/^[a-zA-Z0-9 ]$/.test(key)) return;

        // ✅ Handle comma → add space automatically
        if (key === ',') {
            event.preventDefault();

            const start = input.selectionStart;
            const end = input.selectionEnd;
            const value = input.value;

            input.value = value.slice(0, start) + ', ' + value.slice(end);

            // cursor comma + space ke baad set karo
            input.setSelectionRange(start + 2, start + 2);
            return;
        }

        // ❌ Block everything else
        event.preventDefault();
    }

    isNumberKey(event: any): boolean {
        const charCode = (event.which) ? event.which : event.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    onContactNameInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        if (value.length > 0) {
            const words = value.split(' ');
            const capitalizedWords = words.map(word => {
                if (word.length > 0) {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }
                return word;
            });
            this.row.contactName = capitalizedWords.join(' ');
        }
    }

    isContactNameValid(contactName: string): boolean {
        if (!contactName || contactName.length === 0) return true;
        return contactName[0] === contactName[0].toUpperCase() && !/[0-9]/.test(contactName[0]);
    }

    onPhoneInput(field: 'phone' | 'altPhone', event: Event): void {
        const input = event.target as HTMLInputElement;
        const value = input.value.replace(/\D/g, '');
        this.row[field] = value;
    }

    isPhoneValid(phone: string): boolean {
        if (!phone || phone.length === 0) return true;
        return phone.length === 10;
    }

    onPhoneChange(event: any): void {
        const phone = event?.toString();

        if (phone === '999999999') {
            this.row.contactName = '';
        }
        if (phone === '8888888888') {
            this.row.contactName = 'Customer';
        }
        if (phone === '9999999999') {
            if (this.row.warehouse === "Delivery") {
                this.row.contactName = 'Receiving Dept';
            }
            if (this.row.warehouse === "Pickup") {
                this.row.contactName = 'Shipping Dept';
            }
        }
        this.cdr.markForCheck();
    }

    onCountryCodeChange(event: any): void {
        const newCode = event;
        if (newCode) {
            // Update shared service for session-wide sync (other rows)
            // but NOT the backend profile
            this.shareddta.updateCountryCode(newCode);
        }
    }

    isEmailValid(email: string): boolean {
        if (!email || email.length === 0) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    getTime(): string {
        return new Date().toLocaleTimeString('en-US', { hour: 'numeric', hour12: true, minute: 'numeric' });
    }

    onDelyTimeSet(event: any): void {
        this.row.deliveryTime = event;
    }

    onPickupTimeSet(event: any): void {
        this.row.pickupTime = event;
    }

    removeTimedely(): void {
        this.row.deliveryTime = '';
    }

    removeTime(): void {
        this.row.pickupTime = '';
    }

    onSignReqChange(): void {
        const signatureText = "Customer signature required.";
        let currentNote = this.row.note || '';
        if (this.row.signReq === 'Yes') {
            if (!currentNote.includes(signatureText)) {
                this.row.note = currentNote ? `${signatureText}, ${currentNote}` : signatureText;
            }
        } else {
            if (currentNote.includes(signatureText)) {
                let newNote = currentNote.replace(new RegExp(`${signatureText}(,\\s*)?`, 'g'), '').trim();
                newNote = newNote.replace(/^[,\s\.]+/, '');
                this.row.note = newNote;
            }
        }
    }
    require_delivery_signature: any;
    onWarehouseChange(row: any) {
        this.require_delivery_signature = localStorage.getItem('require_delivery_signature') === 'true';
        if (row.warehouse === 'Delivery' && this.require_delivery_signature) {
            row.signReq = 'Yes';
            this.onSignReqChange(); // 👈 function call
        }
        if (row.warehouse === 'Pickup') {
            row.signReq = 'No';
            this.onSignReqChange(); // 👈 function call
        }
    }

    onDeleteRow(): void {
        this.delete.emit(this.row);
    }

    onAddAddressClick(): void {
        this.openAddressDialog.emit(this.row);
        this.cdr.markForCheck();
    }

    async addNewCompany(): Promise<void> {
        if (!this.row.addressId || this.row.isAddressNew) {
            this.toast.show('warning', 'Please select or add the address first. The address you entered is not valid.');
            return;
        }
        if (!this.row.companyName?.trim()) {
            this.toast.show('warning', 'Company name is required');
            return;
        }

        const confirmed = await this.dialogService.confirm(
            'Add Company',
            `Do you want to add "${this.row.companyName.trim()}" as a new company?`,
            'Add Company',
            'Cancel'
        );

        if (!confirmed?.confirmed) return;

        this.isAddingCompany = true;
        this.cdr.markForCheck();
        const formData = new FormData();
        formData.append('company', this.row.companyName.trim());
        formData.append('address', String(this.row.addressId));
        this.companyService.companynameAdd(formData).subscribe({
            next: (response: any) => {
                this.isAddingCompany = false;
                if (response?.resultCode === '1' || response?.resultDescription) {
                    this.toast.show('success', response.resultDescription || 'Company added successfully');
                    this.row.isCompanyNew = false;
                    this.row.matchingCompanies = [];
                    this.companyAdded.emit(this.row.companyName.trim());
                } else {
                    this.toast.show('error', response?.errorMessage || 'Failed to add company');
                }
                this.cdr.markForCheck();
            },
            error: (err: any) => {
                this.isAddingCompany = false;
                this.toast.show('error', 'Failed to add company. Please try again.');
                this.cdr.markForCheck();
            }
        });
    }

    onDelPUChange(value: any, row: any): void {
        row.delPU = value;   // ✔️ sirf isi row ko update kare
    }

    // Del/PU custom dropdown
    showDelpuDropdown = false;
    visibleDelpuCount = 3;
    computedDelpuOptions: any[] = [];

    get visibleDelpuOptions() {
        return this.computedDelpuOptions.slice(0, this.visibleDelpuCount);
    }

    toggleDelpuDropdown(): void {
        this.showDelpuDropdown = !this.showDelpuDropdown;
        if (this.showDelpuDropdown) {
            this.computedDelpuOptions = this.getFilteredDelpuOptions(this.row);
        }
        this.cdr.markForCheck();
    }

    private getFilteredDelpuOptions(currentRow: BulkOrderRow): any[] {
        const allOptions = this.delpuOptions.map(opt => ({ ...opt, disabled: false }));
        const getDelPU = (row: BulkOrderRow) => (row.delPU !== undefined && row.delPU !== null ? row.delPU + '' : '');

        if (!currentRow.driver_id) {
            const used = this.allRows
                .filter(row => row !== currentRow && !row.driver_id && getDelPU(row) && getDelPU(row) !== 'No' && getDelPU(row) !== '0')
                .map(row => getDelPU(row));
            return allOptions.map(opt => ({
                ...opt,
                disabled: used.includes(opt.value + '') && (getDelPU(currentRow) !== opt.value + '')
            }));
        }

        const currentDriver = currentRow.driver_id;
        const usedByDriver = this.allRows
            .filter(row => row !== currentRow && row.driver_id === currentDriver && getDelPU(row) && getDelPU(row) !== 'No' && getDelPU(row) !== '0')
            .map(row => getDelPU(row));

        return allOptions.map(opt => ({
            ...opt,
            disabled: usedByDriver.includes(opt.value + '') && (getDelPU(currentRow) !== opt.value + '')
        }));
    }

    closeDelpuDropdown(): void {
        setTimeout(() => {
            this.showDelpuDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    selectDelpu(value: number): void {
        this.row.delPU = value;
        this.showDelpuDropdown = false;
        this.onDelPUChange(value, this.row);
        this.cdr.markForCheck();
    }

    loadMoreDelpu(event: Event): void {
        event.stopPropagation();
        this.visibleDelpuCount = Math.min(this.visibleDelpuCount + 3, this.delpuOptions.length);
        this.cdr.markForCheck();
    }
}
